import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Table,
  Box,
  Badge,
  StatusIndicator,
  Button,
  Select,
  ColumnLayout,
  Link,
  Pagination,
} from '@cloudscape-design/components';
import { getAllFeedback, resolveFeedback } from '../services/s3Service';
import { useHistory } from 'react-router-dom';
import { getFundRequestDetailsUrl } from '../utils/urlHelper';

const DECISION_LABELS = {
  recommend_approval: 'Recommend Approval',
  recommend_rejection: 'Recommend Rejection',
  needs_review: 'Needs Review',
};

const PAGE_SIZE = 10;

const getStatus = (item) => {
  if (item.feedbackType === 'AGREE') return 'Resolved';
  if (item.replyDisposition === 'NOT_AN_ISSUE') return 'Resolved';
  if (item.replyDisposition === 'IS_AN_ISSUE' && item.resolvedAt) return 'Resolved';
  return 'Open';
};

const exportFeedbackCsv = (items) => {
  const headers = [
    'Fund Request ID', 'Reviewer', 'Chronos Decision', 'Feedback',
    'Recommended Decision', 'Disagreed Validations', 'Reason',
    'Reply Disposition', 'SIM Ticket URL', 'Status', 'Submitted',
  ];
  const rows = items.map((item) => [
    item.fundRequestId || '',
    item.reviewerAlias || '',
    DECISION_LABELS[item.validationDecision] || item.validationDecision || '',
    item.feedbackType || '',
    DECISION_LABELS[item.recommendedDecision] || item.recommendedDecision || '',
    (item.disagreementValidations || []).join('; '),
    item.disagreementReason || '',
    item.replyDisposition || '',
    item.simTicketUrl || '',
    getStatus(item),
    item.submittedAt || '',
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `feedback-report-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const FeedbackReport = () => {
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canReply, setCanReply] = useState(false);
  const [filterType, setFilterType] = useState(null);
  const [filterReviewer, setFilterReviewer] = useState(null);
  const [filterStatus, setFilterStatus] = useState({ label: 'Open', value: 'Open' });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedReasons, setExpandedReasons] = useState(new Set());
  const [sortingColumn, setSortingColumn] = useState({ sortingField: 'submittedAt' });
  const [sortingDescending, setSortingDescending] = useState(true);
  const [resolvingItems, setResolvingItems] = useState(new Set());
  const history = useHistory();

  useEffect(() => {
    loadFeedback();
  }, []);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllFeedback();
      setFeedbackItems(data.feedback || []);
      setIsAdmin(data.isAdmin || false);
      setCanReply(data.canReplyToFeedback || false);
    } catch (err) {
      console.error('Failed to load feedback:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (fundRequestId, reviewerId) => {
    const itemKey = `${fundRequestId}:${reviewerId}`;
    setResolvingItems((prev) => new Set(prev).add(itemKey));
    try {
      await resolveFeedback(fundRequestId, reviewerId);
      await loadFeedback();
    } catch (err) {
      console.error('Failed to resolve feedback:', err);
    } finally {
      setResolvingItems((prev) => {
        const next = new Set(prev);
        next.delete(itemKey);
        return next;
      });
    }
  };

  // Build unique reviewer options for admin filter
  const reviewerOptions = isAdmin
    ? [...new Set(feedbackItems.map((i) => i.reviewerAlias).filter(Boolean))].map((alias) => ({
        label: alias,
        value: alias,
      }))
    : [];

  const filteredItems = feedbackItems.filter((item) => {
    if (filterType && item.feedbackType !== filterType.value) return false;
    if (filterReviewer && item.reviewerAlias !== filterReviewer.value) return false;
    if (filterStatus && getStatus(item) !== filterStatus.value) return false;
    return true;
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    const field = sortingColumn.sortingField;
    const aVal = a[field] || '';
    const bVal = b[field] || '';
    const cmp = String(aVal).localeCompare(String(bVal));
    return sortingDescending ? -cmp : cmp;
  });
  const totalPages = Math.ceil(sortedItems.length / PAGE_SIZE);
  const paginatedItems = sortedItems.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const resetPage = () => setCurrentPage(1);

  const agreeCount = feedbackItems.filter((i) => i.feedbackType === 'AGREE').length;
  const disagreeCount = feedbackItems.filter((i) => i.feedbackType === 'DISAGREE').length;
  const total = feedbackItems.length;
  const agreementRate = total > 0 ? Math.round((agreeCount / total) * 100) : 0;
  const openCount = feedbackItems.filter((i) => getStatus(i) === 'Open').length;
  const resolvedCount = feedbackItems.filter((i) => getStatus(i) === 'Resolved').length;
  const uniqueReviewers = isAdmin ? new Set(feedbackItems.map((i) => i.reviewerAlias)).size : null;

  const formatDecision = (value) => DECISION_LABELS[value] || value || '—';

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (error) {
    return (
      <Container header={<Header variant="h1">Feedback Report</Header>}>
        <Box textAlign="center" padding="xxl">
          <StatusIndicator type="error">{error}</StatusIndicator>
          <Box padding={{ top: 'm' }}>
            <Button onClick={loadFeedback}>Retry</Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // Build column definitions — add Reviewer column for admins
  const columnDefinitions = [
    {
      id: 'fundRequestId',
      header: 'Fund Request ID',
      cell: (item) => (
        <Link
          onFollow={(e) => {
            e.preventDefault();
            history.push(getFundRequestDetailsUrl(item.fundRequestId));
          }}
        >
          {item.fundRequestId || '—'}
        </Link>
      ),
      sortingField: 'fundRequestId',
      width: 220,
    },
    ...(isAdmin
      ? [
          {
            id: 'reviewerAlias',
            header: 'Reviewer',
            cell: (item) => item.reviewerAlias || '—',
            sortingField: 'reviewerAlias',
          },
        ]
      : []),
    {
      id: 'validationDecision',
      header: 'Chronos Decision',
      cell: (item) => formatDecision(item.validationDecision),
      sortingField: 'validationDecision',
    },
    {
      id: 'feedbackType',
      header: 'Feedback',
      cell: (item) => (
        <Badge color={item.feedbackType === 'AGREE' ? 'green' : 'red'}>
          {item.feedbackType}
        </Badge>
      ),
      sortingField: 'feedbackType',
    },
    {
      id: 'recommendedDecision',
      header: 'Recommended Decision',
      cell: (item) => formatDecision(item.recommendedDecision),
    },
    {
      id: 'disagreementValidations',
      header: 'Disagreed Validations',
      cell: (item) => {
        const validations = item.disagreementValidations || [];
        if (validations.length === 0) return '—';
        return (
          <SpaceBetween direction="horizontal" size="xxs">
            {validations.map((v) => (
              <Badge key={v} color="red">
                {v.replace(/_/g, ' ')}
              </Badge>
            ))}
          </SpaceBetween>
        );
      },
    },
    {
      id: 'disagreementReason',
      header: 'Reason',
      cell: (item) => {
        const reason = item.disagreementReason;
        if (!reason) return '—';
        const MAX_LEN = 120;
        if (reason.length <= MAX_LEN) return reason;
        const itemKey = `${item.fundRequestId}:${item.reviewerId}`;
        const isExpanded = expandedReasons.has(itemKey);
        const toggle = () => {
          setExpandedReasons((prev) => {
            const next = new Set(prev);
            if (next.has(itemKey)) {
              next.delete(itemKey);
            } else {
              next.add(itemKey);
            }
            return next;
          });
        };
        return (
          <Box>
            {isExpanded ? reason : `${reason.substring(0, MAX_LEN)}...`}
            {' '}
            <Link onFollow={(e) => { e.preventDefault(); toggle(); }} variant="primary" fontSize="body-s">
              {isExpanded ? 'Show less' : 'Show more'}
            </Link>
          </Box>
        );
      },
    },
    {
      id: 'replyStatus',
      header: 'Reply',
      cell: (item) => {
        if (item.feedbackType !== 'DISAGREE') return '—';
        if (item.replyDisposition) {
          return (
            <SpaceBetween direction="horizontal" size="xs">
              <Badge color={item.replyDisposition === 'NOT_AN_ISSUE' ? 'blue' : 'red'}>
                {item.replyDisposition === 'NOT_AN_ISSUE' ? 'Not an issue' : 'Is an issue'}
              </Badge>
              {item.simTicketUrl && (
                <Link href={item.simTicketUrl} external variant="primary" target="_blank" rel="noopener noreferrer">
                  Ticket
                </Link>
              )}
            </SpaceBetween>
          );
        }
        if (canReply) {
          return (
            <Button
              variant="normal"
              onClick={() => history.push(getFundRequestDetailsUrl(item.fundRequestId))}
            >
              Reply
            </Button>
          );
        }
        return <Badge color="grey">Pending</Badge>;
      },
      sortingField: 'replyDisposition',
    },
    {
      id: 'submittedAt',
      header: 'Submitted',
      cell: (item) => formatDate(item.submittedAt),
      sortingField: 'submittedAt',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item) => {
        const status = getStatus(item);
        if (status === 'Resolved') {
          return <Badge color="green">Resolved</Badge>;
        }
        return (
          <SpaceBetween direction="horizontal" size="xs">
            <Badge color="red">Open</Badge>
            {canReply && item.replyDisposition === 'IS_AN_ISSUE' && (
              <Button
                variant="inline-link"
                loading={resolvingItems.has(`${item.fundRequestId}:${item.reviewerId}`)}
                onClick={() => handleResolve(item.fundRequestId, item.reviewerId)}
              >
                Mark Resolved
              </Button>
            )}
          </SpaceBetween>
        );
      },
      sortingField: 'resolvedAt',
    },
  ];

  return (
    <SpaceBetween size="l">
      <Header variant="h1">
        Feedback Report {isAdmin && <Badge color="blue">Admin View</Badge>}
      </Header>

      {/* Summary Cards */}
      <Container>
        <ColumnLayout columns={isAdmin ? 7 : 6} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Total Reviews</Box>
            <Box variant="awsui-value-large">{total}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Open</Box>
            <Box variant="awsui-value-large" color="text-status-error">{openCount}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Resolved</Box>
            <Box variant="awsui-value-large" color="text-status-success">{resolvedCount}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Agree</Box>
            <Box variant="awsui-value-large" color="text-status-success">{agreeCount}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Disagree</Box>
            <Box variant="awsui-value-large" color="text-status-warning">{disagreeCount}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Agreement Rate</Box>
            <Box variant="awsui-value-large">{agreementRate}%</Box>
          </div>
          {isAdmin && (
            <div>
              <Box variant="awsui-key-label">Reviewers</Box>
              <Box variant="awsui-value-large">{uniqueReviewers}</Box>
            </div>
          )}
        </ColumnLayout>
      </Container>

      {/* Feedback Table */}
      <Table
        header={
          <Header
            variant="h2"
            counter={`(${filteredItems.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Select
                  selectedOption={filterStatus}
                  onChange={({ detail }) => { setFilterStatus(detail.selectedOption); resetPage(); }}
                  options={[
                    { label: 'Open', value: 'Open' },
                    { label: 'Resolved', value: 'Resolved' },
                  ]}
                  placeholder="Filter by status"
                  clearAriaLabel="Clear filter"
                  selectedAriaLabel="Selected"
                  isClearable
                />
                <Select
                  selectedOption={filterType}
                  onChange={({ detail }) => { setFilterType(detail.selectedOption); resetPage(); }}
                  options={[
                    { label: 'Agree', value: 'AGREE' },
                    { label: 'Disagree', value: 'DISAGREE' },
                  ]}
                  placeholder="Filter by feedback"
                  clearAriaLabel="Clear filter"
                  selectedAriaLabel="Selected"
                  isClearable
                />
                {isAdmin && reviewerOptions.length > 0 && (
                  <Select
                    selectedOption={filterReviewer}
                    onChange={({ detail }) => { setFilterReviewer(detail.selectedOption); resetPage(); }}
                    options={reviewerOptions}
                    placeholder="Filter by reviewer"
                    clearAriaLabel="Clear filter"
                    selectedAriaLabel="Selected"
                    isClearable
                  />
                )}
                <Button iconName="refresh" onClick={loadFeedback} loading={loading}>
                  Refresh
                </Button>
                <Button iconName="download" onClick={() => exportFeedbackCsv(sortedItems)}>
                  Export CSV
                </Button>
              </SpaceBetween>
            }
          >
            Feedback History
          </Header>
        }
        loading={loading}
        loadingText="Loading feedback..."
        resizableColumns
        wrapLines
        items={paginatedItems}
        empty={
          <Box textAlign="center" padding="xxl">
            <Box variant="h3" color="text-status-inactive">No feedback submitted yet</Box>
            <Box variant="p" color="text-body-secondary" padding={{ top: 's' }}>
              Review fund requests and submit feedback to see your history here.
            </Box>
          </Box>
        }
        columnDefinitions={columnDefinitions}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => {
          setSortingColumn(detail.sortingColumn);
          setSortingDescending(detail.isDescending);
          setCurrentPage(1);
        }}
        pagination={
          totalPages > 1 ? (
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={totalPages}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
            />
          ) : null
        }
      />
    </SpaceBetween>
  );
};

export default FeedbackReport;
